/** Shared modal header + close button layout (aligned with admin portal). */

export const MODAL_HEADER_LAYOUT_CLASS =
	"flex items-center justify-between gap-3 min-h-[5rem] flex-shrink-0";

export const MODAL_HEADER_SURFACE_CLASS = "bg-primary text-primary-foreground px-5 py-5 shrink-0";

export const MODAL_TITLE_CLASS = "text-lg font-semibold text-primary-foreground leading-snug sm:text-xl";

export const MODAL_HEADER_BACK_BUTTON_CLASS =
	"-ml-1 rounded-full p-1 text-primary-foreground transition-colors hover:bg-white/15";

export const MODAL_CLOSE_BUTTON_CLASS =
	"inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-black/10 bg-[#f3f3f3] text-[#161616] shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-colors hover:bg-white hover:border-black/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50";

export const MODAL_CLOSE_BUTTON_OVERLAY_CLASS =
	"absolute top-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-[#f3f3f3] text-[#161616] shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-colors hover:bg-white hover:border-black/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50";

export const MODAL_CLOSE_ICON_SIZE = 20;

export const MODAL_BODY_DEFAULT_PADDING = "p-6";

/** Body when a footer is present — symmetric vertical padding; footer sits flush below (no extra gap). */
export const MODAL_BODY_WITH_FOOTER_PADDING = "px-6 py-6";

/** Footer band — horizontal + bottom padding only. */
export const MODAL_FOOTER_PADDING = "px-6 pb-6 pt-0";

export const isStandardModalPadding = (padding: string): boolean =>
	padding === "p-6" || padding === MODAL_BODY_DEFAULT_PADDING;

/** Matches admin portal committee / form modals (`rounded-lg`). */
export const MODAL_BORDER_RADIUS = "8px";

export const MODAL_BORDER_RADIUS_CLASS = "rounded-lg";

export const MODAL_BORDER_RADIUS_RESPONSIVE = { xs: 0, sm: MODAL_BORDER_RADIUS } as const;

/** Logout confirmation — content-sized height like admin portal. */
export const LOGOUT_MODAL_SHELL = {
	width: "max-w-lg",
	height: "h-auto",
	rounded: MODAL_BORDER_RADIUS_CLASS,
	padding: MODAL_BODY_DEFAULT_PADDING,
} as const;

const LARGE_DETAIL_MODAL_HEIGHT = "max-h-[min(94vh,52rem)]" as const;

/** Large job-creation modals — same shell as admin `ConstitutedCommitteeModal`. */
export const JOB_MODAL_SHELL = {
	width: "max-w-[55rem]",
	height: LARGE_DETAIL_MODAL_HEIGHT,
	rounded: MODAL_BORDER_RADIUS_CLASS,
	padding: "p-0",
	zIndex: "z-[1300]",
} as const;

/** Standard form modals (~576px). */
export const CONTENT_MODAL_SHELL = {
	width: "max-w-xl",
	height: "max-h-[calc(100dvh-2rem)]",
	rounded: MODAL_BORDER_RADIUS_CLASS,
	padding: "p-0",
	zIndex: "z-[1300]",
} as const;

/** Candidate / application detail (skills, cover letter, actions). */
export const CANDIDATE_DETAIL_MODAL_SHELL = {
	width: "max-w-3xl",
	height: LARGE_DETAIL_MODAL_HEIGHT,
	rounded: MODAL_BORDER_RADIUS_CLASS,
	padding: "p-0",
	zIndex: "z-[1300]",
} as const;

/** Large preview modals (job preview, CV, ad). */
export const PREVIEW_MODAL_SHELL = {
	width: "max-w-4xl",
	height: "max-h-[calc(100dvh-2rem)]",
	rounded: MODAL_BORDER_RADIUS_CLASS,
	padding: "p-0",
	zIndex: "z-[1300]",
} as const;

/** Table filter modal. */
export const FILTER_MODAL_SHELL = {
	width: "max-w-3xl",
	height: "max-h-[calc(100dvh-2rem)]",
	rounded: MODAL_BORDER_RADIUS_CLASS,
	padding: "p-0",
	zIndex: "z-[1300]",
} as const;

/** Header profile modal — two-column business / contact form. */
export const PROFILE_MODAL_SHELL = {
	width: "max-w-3xl",
	height: LARGE_DETAIL_MODAL_HEIGHT,
	rounded: MODAL_BORDER_RADIUS_CLASS,
	padding: MODAL_BODY_DEFAULT_PADDING,
} as const;

export const MODAL_DIALOG_PAPER_SX = {
	borderRadius: MODAL_BORDER_RADIUS,
	overflow: "hidden",
	border: "none",
} as const;

export const MODAL_DIALOG_PAPER_SX_RESPONSIVE = {
	borderRadius: MODAL_BORDER_RADIUS_RESPONSIVE,
	overflow: "hidden",
	border: "none",
} as const;

export const modalHeaderClassName = `${MODAL_HEADER_LAYOUT_CLASS} ${MODAL_HEADER_SURFACE_CLASS}`;

export const modalHeaderTitleClassName = MODAL_TITLE_CLASS;

export const modalCloseButtonClassName = MODAL_CLOSE_BUTTON_CLASS;
